import type { User } from "firebase/auth";
import type {
  BillingAccount,
  CloneVoice,
  CreditPack,
  GenerationSettings,
  SubscriptionPlan,
  Usage,
} from "./types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function authHeaders(user: User): Promise<Headers> {
  const headers = new Headers();
  headers.set("Authorization", `Bearer ${await user.getIdToken()}`);
  return headers;
}

async function parseError(response: Response): Promise<never> {
  let message = "We couldn’t complete that request. Please try again.";
  try {
    const payload = (await response.json()) as {
      detail?: string | { message?: string };
      error?: string;
    };
    if (typeof payload.detail === "string") message = payload.detail;
    if (typeof payload.detail === "object" && payload.detail?.message) {
      message = payload.detail.message;
    }
    if (payload.error) message = payload.error;
  } catch {
    // Keep the calm fallback instead of exposing proxy internals.
  }
  throw new ApiError(message, response.status);
}

function uncachedApiPath(path: string): string {
  return `${path}?_surayomi_client=${encodeURIComponent(crypto.randomUUID())}`;
}

export async function getUsage(user: User): Promise<Usage> {
  const response = await fetch(uncachedApiPath("/api/usage"), {
    headers: await authHeaders(user),
    cache: "no-store",
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<Usage>;
}

export async function getBillingAccount(user: User): Promise<BillingAccount> {
  const response = await fetch(uncachedApiPath("/api/billing"), {
    headers: await authHeaders(user),
    cache: "no-store",
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<BillingAccount>;
}

async function billingPost<T>(
  user: User,
  path: string,
  payload: unknown,
  idempotent = true,
): Promise<T> {
  const headers = await authHeaders(user);
  headers.set("Content-Type", "application/json");
  if (idempotent) headers.set("Idempotency-Key", crypto.randomUUID());
  const response = await fetch(uncachedApiPath(path), {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<T>;
}

export async function createSubscriptionCheckout(
  user: User,
  plan: SubscriptionPlan,
  returnPath: "/" | "/ja/",
): Promise<string> {
  const result = await billingPost<{ url: string }>(
    user,
    "/api/billing/checkout",
    { plan, return_path: returnPath },
  );
  return result.url;
}

export async function createCreditCheckout(
  user: User,
  pack: CreditPack,
  returnPath: "/" | "/ja/",
): Promise<string> {
  const result = await billingPost<{ url: string }>(
    user,
    "/api/billing/credits",
    { pack, return_path: returnPath },
  );
  return result.url;
}

export async function createBillingPortal(
  user: User,
  returnPath: "/" | "/ja/",
): Promise<string> {
  const result = await billingPost<{ url: string }>(
    user,
    "/api/billing/portal",
    { return_path: returnPath },
  );
  return result.url;
}

export async function syncBillingCheckout(
  user: User,
  sessionId: string,
): Promise<void> {
  await billingPost<{ status: string }>(
    user,
    "/api/billing/sync",
    { session_id: sessionId },
    false,
  );
}

export async function getCloneVoices(user: User): Promise<CloneVoice[]> {
  const response = await fetch(uncachedApiPath("/api/voices"), {
    headers: await authHeaders(user),
    cache: "no-store",
  });
  if (!response.ok) return parseError(response);
  const payload = (await response.json()) as { voices: CloneVoice[] };
  return payload.voices;
}

export async function createCloneVoice(
  user: User,
  data: {
    name: string;
    language: string;
    referenceText: string;
    audio: File;
  },
): Promise<CloneVoice> {
  const form = new FormData();
  form.set("name", data.name);
  form.set("language", data.language);
  form.set("reference_text", data.referenceText);
  form.set("audio", data.audio);
  const response = await fetch("/api/voices", {
    method: "POST",
    headers: await authHeaders(user),
    body: form,
  });
  if (!response.ok) return parseError(response);
  return response.json() as Promise<CloneVoice>;
}

export async function deleteCloneVoice(
  user: User,
  voiceId: string,
): Promise<void> {
  const response = await fetch(`/api/voices/${encodeURIComponent(voiceId)}`, {
    method: "DELETE",
    headers: await authHeaders(user),
  });
  if (!response.ok) return parseError(response);
}

export async function synthesize(
  user: User,
  text: string,
  settings: GenerationSettings,
): Promise<{ blob: Blob; filename: string; remaining?: number }> {
  const headers = await authHeaders(user);
  headers.set("Content-Type", "application/json");
  const response = await fetch("/api/speech", {
    method: "POST",
    headers,
    body: JSON.stringify({
      text,
      engine: settings.engine,
      speaker: settings.engine === "custom" ? settings.speaker : undefined,
      voice_id: settings.engine === "clone" ? settings.voiceId : undefined,
      language: settings.language,
      instruct: settings.prompt || undefined,
      speed: settings.speed,
      temperature: settings.temperature,
      top_p: settings.topP,
      top_k: settings.topK,
      repetition_penalty: settings.repetitionPenalty,
      seed: settings.seed,
      response_format: settings.format,
    }),
  });
  if (!response.ok) return parseError(response);
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    disposition.match(/filename="?([^"]+)"?/)?.[1] ??
    `surayomi-${Date.now()}.${settings.format}`;
  const remainingHeader = response.headers.get("X-Quota-Remaining");
  return {
    blob: await response.blob(),
    filename,
    remaining: remainingHeader ? Number(remainingHeader) : undefined,
  };
}
